// KASSE_ABO_v1 (27.09.2026) -- Shearly Kasse als Zusatz-Abo, Stufe 1: Karte in „Mein Abo“, Fenster „Kasse anfragen“
// (Kurzfassung, voller Wortlaut, PDF, eingetippter Name, zwei Häkchen) und Status-Kasten für den Admin.
// Wortlaut, Fassung und Prüfsummen kommen vom Server (/api/kasse/offer), die Zustimmung prüft und speichert der
// Server (Register terms_acceptances, append-only). Alle Komponenten auf Modul-Ebene (Lehre EMP_MODAL_STABLE_v1).
// KASSE_ORDER_WORDING_v1 (27.09.2026, Toby „knopf bestellen“): sichtbar „bestellen“ statt „anfragen“.
// KASSE_SETUP_WAIVER_v1: Kurzfassung + Hinweis kommen je Salon vom Server (erlassene Einrichtung → eigene Zeile).
import { useEffect, useState } from "../core/vendor.js";
import { api } from "../core/api.js";
import { COLORS } from "../core/config.jsx";

const fmtDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" }) + " um "
    + d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }) + " Uhr";
};

const nameOk = (s) => { const t = String(s || "").replace(/\s+/g, " ").trim(); return t.length >= 5 && t.includes(" "); };

// Wortlaut der Bedingungen (Markdown-Light: „# “ Titel, „## “ Paragraf, „1. “ Aufzählung, sonst Absatz)
export const KasseTermsText = ({ text }) => (
  <div>
    {String(text || "").split("\n").filter(l => l.trim()).map((l, i) => {
      const s = l.trim();
      if (s.startsWith("# ")) return <div key={i} style={{ fontSize: 16, fontWeight: 800, margin: "0 0 2px" }}>{s.slice(2)}</div>;
      if (s.startsWith("## ")) return <div key={i} style={{ fontSize: 13.5, fontWeight: 700, margin: "12px 0 4px" }}>{s.slice(3)}</div>;
      if (s.startsWith("Fassung vom")) return <div key={i} style={{ fontSize: 12.5, color: COLORS.textLight, marginBottom: 2 }}>{s}</div>;
      if (s.startsWith("Anbieter:")) return <div key={i} style={{ fontSize: 11.5, color: COLORS.textLight, marginBottom: 6 }}>{s}</div>;
      if (/^\d+\. /.test(s)) return <div key={i} style={{ fontSize: 12.5, lineHeight: 1.55, paddingLeft: 14, marginBottom: 2 }}>{s}</div>;
      return <p key={i} style={{ fontSize: 12.5, lineHeight: 1.55, margin: "0 0 6px" }}>{s}</p>;
    })}
  </div>
);

export const KasseRequestModal = ({ offer, onClose, onDone }) => {
  const [name, setName] = useState("");
  const [c1, setC1] = useState(false);
  const [c2, setC2] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState("");
  const ready = c1 && c2 && nameOk(name) && !busy;
  const submit = async () => {
    if (!ready) return;
    setBusy(true); setErr("");
    const r = await api.requestKasse({
      version: offer.version, text_sha256: offer.text_sha256,
      checkbox_terms: c1 === true, checkbox_auth: c2 === true, full_name: name,
    });
    setBusy(false);
    if (r && r.ok) { setDone((r.data && r.data.done_text) || offer.done_text || "Danke!"); onDone && onDone(); return; }
    setErr((r && r.data && r.data.error) || "Bestellung fehlgeschlagen. Bitte später erneut versuchen.");
  };
  const box = { background: "var(--card)", color: COLORS.text, borderRadius: 16, padding: 22, width: "100%", maxWidth: 620,
                maxHeight: "92vh", overflowY: "auto", boxShadow: "0 25px 50px rgba(0,0,0,0.25)" };
  const check = { display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13.5, lineHeight: 1.45, cursor: "pointer", padding: "6px 0" };
  return (
    <div data-feature="KASSE_ABO_v1" onClick={e => { if (e.target === e.currentTarget && !busy) onClose(); }}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 10040, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={box}>
        <h3 style={{ margin: "0 0 10px", fontSize: 18, fontWeight: 800 }}>Shearly Kasse bestellen</h3>
        {done ? (
          <>
            <div style={{ padding: "14px 16px", borderRadius: 10, background: "rgba(34,197,94,0.12)", color: COLORS.text, fontSize: 14, lineHeight: 1.55 }}>✓ {done}</div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
              <button type="button" onClick={onClose} style={{ padding: "10px 18px", borderRadius: 8, border: "none", background: COLORS.primary, color: "#fff", fontWeight: 700, cursor: "pointer" }}>Schließen</button>
            </div>
          </>
        ) : (
          <>
            <ul style={{ margin: "0 0 6px", paddingLeft: 18, fontSize: 13.5, lineHeight: 1.6 }}>
              {(offer.summary || []).map((x, i) => <li key={i}>{x}</li>)}
            </ul>
            <div style={{ fontSize: 12, color: COLORS.textLight, marginBottom: 12 }}>
              {offer.note || "Was du individuell mit Shearly vereinbart hast (z. B. zur Einrichtungsgebühr), gilt weiter."}
            </div>
            <div style={{ border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: "12px 14px", maxHeight: "38vh", overflowY: "auto", background: "var(--cardHover)" }}>
              <KasseTermsText text={offer.text} />
            </div>
            <div style={{ margin: "8px 0 14px", fontSize: 13 }}>
              <a href={offer.pdf_url} target="_blank" rel="noopener noreferrer" style={{ color: COLORS.primary, fontWeight: 600 }}>📄 Bedingungen als PDF herunterladen</a>
            </div>
            <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Dein Vor- und Nachname</label>
            <input type="text" value={name} onChange={e => setName(e.target.value.slice(0, 120))} placeholder="Vor- und Nachname" autoComplete="name"
              style={{ width: "100%", boxSizing: "border-box", padding: "10px 12px", borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 14, background: "var(--card)", color: COLORS.text, marginBottom: 10 }} />
            <label style={check}>
              <input type="checkbox" checked={c1} onChange={e => setC1(e.target.checked)} style={{ marginTop: 3, accentColor: COLORS.primary }} />
              <span>{offer.checkbox_terms}</span>
            </label>
            <label style={check}>
              <input type="checkbox" checked={c2} onChange={e => setC2(e.target.checked)} style={{ marginTop: 3, accentColor: COLORS.primary }} />
              <span>{offer.checkbox_auth}</span>
            </label>
            {err && <div style={{ fontSize: 13, color: COLORS.danger, marginTop: 8 }}>{err}</div>}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16, flexWrap: "wrap" }}>
              <button type="button" onClick={onClose} disabled={busy}
                style={{ padding: "10px 16px", borderRadius: 8, border: `1px solid ${COLORS.border}`, background: "transparent", color: COLORS.text, cursor: "pointer", fontWeight: 600 }}>Abbrechen</button>
              <button type="button" onClick={submit} disabled={!ready}
                style={{ padding: "10px 18px", borderRadius: 8, border: "none", background: ready ? COLORS.primary : "var(--cardHover)", color: ready ? "#fff" : COLORS.textLight, cursor: ready ? "pointer" : "not-allowed", fontWeight: 700 }}>
                {busy ? "Sende …" : offer.button}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Karte in „Mein Abo“ -- nur für die Inhaberin / den Inhaber
export const KasseCard = ({ currentUser }) => {
  const [offer, setOffer] = useState(null);
  const [open, setOpen] = useState(false);
  const isOwner = !!(currentUser && currentUser.role === "owner");
  const load = () => api.getKasseOffer().then(r => { if (r && r.ok) setOffer(r.data); });
  useEffect(() => { if (isOwner) load(); }, []);
  if (!isOwner || !offer) return null;
  const st = offer.status || {};
  const wrap = { margin: "0 0 16px", padding: "14px 16px", borderRadius: 12, border: `1px solid ${COLORS.border}`, background: "var(--card)", color: COLORS.text };
  if (st.requested_at) {
    return (
      <div data-feature="KASSE_ABO_v1" style={wrap}>
        <div style={{ fontWeight: 700, fontSize: 14.5 }}>✓ Shearly Kasse bestellt</div>
        <div style={{ fontSize: 13, color: COLORS.textLight, marginTop: 4, lineHeight: 1.5 }}>
          Am {fmtDateTime(st.requested_at)}{st.full_name ? ` von ${st.full_name}` : ""}. Wir melden uns zur Einrichtung; der Vertrag beginnt mit der Freischaltung.{" "}
          <a href={offer.pdf_url} target="_blank" rel="noopener noreferrer" style={{ color: COLORS.primary, fontWeight: 600 }}>Bedingungen (PDF)</a>
        </div>
      </div>
    );
  }
  if (!offer.can_request) return null;
  return (
    <div data-feature="KASSE_ABO_v1" style={{ ...wrap, display: "flex", gap: 12, alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
      <div style={{ minWidth: 220, flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: 14.5 }}>🧾 Shearly Kasse</div>
        <div style={{ fontSize: 13, color: COLORS.textLight, marginTop: 3, lineHeight: 1.5 }}>
          Kasse mit TSE für deinen Salon: Bons, Tagesabschluss, DATEV-Export. 24,90 € netto im Monat, 12 Monate Mindestlaufzeit.
          {offer.setup_fee_waived ? " Die Einrichtung ist für euch erlassen." : ""}
        </div>
      </div>
      <button type="button" onClick={() => setOpen(true)}
        style={{ padding: "10px 16px", borderRadius: 8, border: "none", background: COLORS.primary, color: "#fff", fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>Kasse bestellen</button>
      {open && <KasseRequestModal offer={offer} onClose={() => { setOpen(false); load(); }} onDone={load} />}
    </div>
  );
};

// Status-Kasten in der Admin-Salonansicht (nur lesend)
export const AdminKasseBox = ({ salonId }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!salonId) return;
    api.request(`/admin/salons/${salonId}/kasse`).then(r => { if (r && r.ok) setData(r.data); });
  }, [salonId]);
  if (!data) return null;
  const st = data.status || {};
  return (
    <div data-feature="KASSE_ABO_v1" style={{ marginTop: 6, padding: 12, border: `1px solid ${COLORS.border}`, borderRadius: 8, background: "var(--cardHover)" }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.primary, marginBottom: 4 }}>Shearly Kasse – Bestellung (Kassen-Abo)</div>
      {st.requested_at ? (
        <div style={{ fontSize: 12.5, lineHeight: 1.55, color: COLORS.text }}>
          Bestellt am {fmtDateTime(st.requested_at)} von <b>{st.full_name || "–"}</b> (Login {st.user_email || "–"}, IP {st.ip || "–"}) · Nachweis-Nr. {st.acceptance_id} · Fassung {data.terms_version}
          {st.admin_ip_warning && <div style={{ marginTop: 6, color: COLORS.danger, fontWeight: 700 }}>⚠️ Zustimmung kam von einer Admin-IP – vermutlich keine Zustimmung des Salons.</div>}
        </div>
      ) : (
        <div style={{ fontSize: 12.5, color: COLORS.textLight }}>Noch nicht bestellt (Knopf „Kasse bestellen“ in „Mein Abo“ des Salons).</div>
      )}
      {st.setup_fee_waived && (
        <div data-feature="KASSE_SETUP_WAIVER_v1" style={{ marginTop: 6, fontSize: 12.5, color: COLORS.text }}>
          Einrichtungsgebühr (149 € netto) <b>erlassen</b> seit {fmtDateTime(st.setup_fee_waived_at)}{st.setup_fee_waived_reason ? ` – ${st.setup_fee_waived_reason}` : ""}
        </div>
      )}
    </div>
  );
};
